'use client';

import Card from '@cobliteam/ui/components/Card';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@cobliteam/ui-business/components/Collapsible';
import type { ToolUIPart } from 'ai';
import type { ComponentProps, ReactNode } from 'react';
import { isValidElement } from 'react';
import { toolNameMap } from '../../types';
import { CodeBlock, CodeBlockCopyButton } from './CodeBlock';

export type ToolProps = ComponentProps<typeof Collapsible> & {
  isDebug?: boolean;
};

export const Tool = ({ className, isDebug = false, ...props }: ToolProps) => (
  <Card className={cn(isDebug ? 'border-gray-4' : 'border-none bg-transparent shadow-none', 'not-prose mb-4 w-full overflow-hidden p-0', className)}>
    <Collapsible {...props} />
  </Card>
);

export type ToolHeaderProps = {
  isStreaming?: boolean;
  isDebug?: boolean;
  title?: string;
  type: ToolUIPart['type'];
  state: ToolUIPart['state'];
  className?: string;
};

const getStatusTag = (status: ToolUIPart['state'], isStreaming: boolean) => {
  // const labels: Record<ToolUIPart['state'], string> = {
  //   'input-streaming': 'Pendente',
  //   'input-available': 'Processando...',
  //   'output-available': 'Concluído',
  //   'output-error': 'Erro',
  // };

  const icons: Record<any, ReactNode> = {
    'input-streaming': <Icon name='clock' size='sm' className='animate-pulse text-gray-10' />,
    'input-available': <Icon name='loader-circle' size='sm' className='animate-spin text-gray-9' />,
    'output-available': <Icon name='circle-check' size='sm' className='text-success-10' />,
    'output-error': <Icon name='circle-x' size='sm' className='text-danger-10' />,
  };

  // const tagVariants: Record<ToolUIPart['state'], { variant: 'contained' | 'outlined' | 'surface' | 'default'; color: 'default' | 'success' | 'danger' | 'warning' }> = {
  //   'input-streaming': { variant: 'surface', color: 'default' },
  //   'input-available': { variant: 'surface', color: 'default' },
  //   'output-available': { variant: 'surface', color: 'success' },
  //   'output-error': { variant: 'surface', color: 'danger' },
  // };

  return isStreaming ? icons[status] : icons['output-available'];
};

export const ToolHeader = ({ className, title, type, state, isStreaming = false, isDebug = false, ...props }: ToolHeaderProps) => {
  const toolName = toolNameMap[type] || 'Processando dados...';
  return (
    <CollapsibleTrigger
      className={cn(
        'flex w-full items-center justify-between gap-4 border-gray-4 transition-colors',
        isDebug ? 'cursor-pointer border bg-transparent hover:bg-gray-3' : 'cursor-default',
        className
      )}
      {...props}
    >
      <div className='flex items-center gap-3'>
        {getStatusTag(state, isStreaming)}
        <Typography variant='text-md' className='text-gray-11 italic'>
          {title ?? toolName} {isDebug && `(${type})`}
        </Typography>
      </div>
      {isDebug && <Icon name='chevron-down' size='sm' className='shrink-0 text-gray-10 transition-transform group-data-[state=open]:rotate-180' />}
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 outline-none data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<'div'> & {
  input: ToolUIPart['input'];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div className={cn('space-y-3 border-gray-4 border-b p-4', className)} {...props}>
    <Typography variant='caption-xs-semibold' className='text-gray-10 uppercase tracking-wide'>
      Parameters
    </Typography>
    <div className='relative'>
      <CodeBlock code={JSON.stringify(input, null, 2)} language='json' className='border-gray-5'>
        <CodeBlockCopyButton />
      </CodeBlock>
    </div>
  </div>
);

export type ToolOutputProps = ComponentProps<'div'> & {
  output: ToolUIPart['output'];
  errorText: ToolUIPart['errorText'];
};

export const ToolOutput = ({ className, output, errorText, ...props }: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  const isError = !!errorText;

  let Output = <div>{output as ReactNode}</div>;

  if (typeof output === 'object' && !isValidElement(output)) {
    Output = (
      <CodeBlock code={JSON.stringify(output, null, 2)} language='json' className={isError ? 'border-danger-5' : 'border-gray-5'}>
        <CodeBlockCopyButton />
      </CodeBlock>
    );
  } else if (typeof output === 'string') {
    Output = (
      <CodeBlock code={output} language='json' className={isError ? 'border-danger-5' : 'border-gray-5'}>
        <CodeBlockCopyButton />
      </CodeBlock>
    );
  }

  return (
    <div className={cn('space-y-3 p-4', className)} {...props}>
      <Typography variant='caption-xs-semibold' className='uppercase tracking-wide' color={isError ? 'danger' : 'default'}>
        {isError ? 'Error' : 'Result'}
      </Typography>
      {errorText && (
        <div className='rounded-md border border-danger-5 bg-danger-3 p-3'>
          <Typography variant='text-sm' className='text-danger-11'>
            {errorText}
          </Typography>
        </div>
      )}
      {!isError && <div className='relative'>{Output}</div>}
      {isError && typeof output === 'object' && !isValidElement(output) && <div className='relative'>{Output}</div>}
      {isError && typeof output === 'string' && <div className='relative'>{Output}</div>}
    </div>
  );
};
